import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, OnInit, OnChanges, SimpleChanges, Injector } from '@angular/core';

import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule, ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { MatMenu } from '@angular/material/menu';
import { DeleteIconButton } from '../delete-icon-button/delete-icon-button';
import {
  emailFormValidator,
  blockAccentedCharsOnKeyDown,
  phoneFormValidator,
  blockExcessPhoneDigitsOnKeyDown,
  cleanPhoneValue,
  formatPhoneNumber,
  calculateCursorPosition
} from '../../validators';

@Component({
  selector: 'app-small-input-menu',
  standalone: true,
  imports: [
    MatMenuModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FormsModule,
    ReactiveFormsModule,
    DeleteIconButton
],
  templateUrl: './small-input-menu.component.html',
  styleUrls: ['./small-input-menu.component.scss']
})
export class SmallInputMenuComponent implements OnInit, OnChanges {
  @Input() menuTrigger?: MatMenuTrigger; // Optional trigger from parent
  @Input() inputType: 'phone' | 'email' | 'official' | 'notes' = 'phone'; // Input type selector
  @Input() label?: string; // Custom label
  @Input() placeholder?: string; // Custom placeholder
  @Input() inputValue: string = ''; // Two-way binding value
  @Input() showDeleteButton: boolean = false; // Show delete button for specific contexts
  @Input() notePresets: Array<{value: string, label: string}> | null = null; // Preset opciók (pl. SZ/TR/B + Egyéni)
  @Input() showClearPlanCheckbox: boolean = false; // Tervszámok törlése checkbox megjelenítése
  
  @Output() valueAdded = new EventEmitter<string>(); // Generic value added event
  @Output() phoneAdded = new EventEmitter<string>(); // Backward compatibility
  @Output() emailAdded = new EventEmitter<string>(); // New email event
  @Output() officialAdded = new EventEmitter<string>(); // New official event
  @Output() cancelled = new EventEmitter<void>();
  @Output() inputValueChange = new EventEmitter<string>(); // For two-way binding
  @Output() deleteClicked = new EventEmitter<void>(); // Delete button event
  @Output() clearPlanRequested = new EventEmitter<void>(); // Tervszámok törlése esemény

  /** Kiválasztott szín notes típusnál ('orange' = alapértelmezett, 'red' = piros) */
  /** Tervszámok törlése checkbox állapota */
  clearPlanChecked: boolean = false;
  selectedNoteColor: 'orange' | 'red' = 'orange';
  /** Kiválasztott preset opció ('SZ'/'TR'/'B'/'Egyéni'/null) */
  selectedPreset: string | 'Egyéni' | null = null;

  @ViewChild('inputField') inputField!: ElementRef<HTMLInputElement>;
  @ViewChild('smallInputMenu') smallInputMenu!: MatMenu;

  // Public getter for external access to the menu
  get menu(): MatMenu {
    return this.smallInputMenu;
  }

  inputControl = new FormControl('', [
    Validators.required,
    this.dynamicValidator.bind(this)
  ]);

  ngOnInit() {
    // Első inicializálás csak ha nincs menü trigger (közvetlenül használt komponens)
    // A menü opened/closed események a template-ből jönnek
  }

  /** Menü megnyitáskor hívódik a parent matMenuTrigger (menuOpened) eseményéből */
  onMenuOpened() {
    this.initializeFormState();
  }

  /** Menü bezáráskor hívódik a parent matMenuTrigger (menuClosed) eseményéből */
  onMenuClosed() {
    this.resetForm();
    this.inputValueChange.emit('');
  }

  /** Szín kiválasztása a notes szín-választóban */
  selectNoteColor(color: 'orange' | 'red') {
    this.selectedNoteColor = color;
  }

  /** Preset opció kiválasztása */
  selectPreset(preset: string | 'Egyéni') {
    this.selectedPreset = preset;
    if (preset === 'Egyéni') {
      setTimeout(() => {
        if (this.inputField) {
          this.inputField.nativeElement.focus();
        }
      }, 50);
    }
  }

  /** Alap állapot beállítása - minden menü megnyitásnál lefut */
  initializeFormState() {
    if (this.inputValue && this.inputValue.trim() !== '') {
      // Ha van bejövő érték (pl. szerkesztés mód), azt állítsuk be
      if (this.inputType === 'notes') {
        // Szín prefix kinyerése
        let rawValue = this.inputValue;
        if (this.inputValue.startsWith('red:')) {
          this.selectedNoteColor = 'red';
          rawValue = this.inputValue.substring(4);
        } else {
          this.selectedNoteColor = 'orange';
        }
        // Preset detektálás
        if (this.notePresets !== null) {
          const matchingPreset = this.notePresets.find(p => p.value === rawValue);
          if (matchingPreset) {
            this.selectedPreset = rawValue;
            this.inputControl.reset();
          } else {
            this.selectedPreset = 'Egyéni';
            this.inputControl.setValue(rawValue);
          }
        } else {
          this.inputControl.setValue(rawValue);
        }
      } else {
        this.inputControl.setValue(this.inputValue);
      }
    } else if (this.inputType === 'phone') {
      // Telefonszámnál mindig +36 előtag
      this.inputControl.setValue('+36 ');
    } else {
      this.selectedNoteColor = 'orange'; // Alapértelmezett szín visszaállítása
      this.selectedPreset = null;
      this.inputControl.reset();
    }
    
    // Validációs állapot törlése - ne legyen piros keret megnyitáskor
    this.inputControl.markAsUntouched();
    this.inputControl.markAsPristine();
    
    // Focus input when menu opens (csak ha nincs preset mód, vagy Egyéni van kiválasztva)
    setTimeout(() => {
      if (this.inputField && (this.notePresets === null || this.selectedPreset === 'Egyéni')) {
        this.inputField.nativeElement.focus();
      }
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges) {
    // Telefonszámnál kihagy: a formázás az onPhoneInput()-ban történik
    if (changes['inputValue'] && !changes['inputValue'].isFirstChange()) {
      if (this.inputType === 'phone') return;
      const newVal = changes['inputValue'].currentValue;
      if (newVal && newVal.trim() !== '') {
        if (this.inputType === 'notes') {
          let rawValue = newVal;
          if (newVal.startsWith('red:')) {
            this.selectedNoteColor = 'red';
            rawValue = newVal.substring(4);
          } else {
            this.selectedNoteColor = 'orange';
          }
          if (this.notePresets !== null) {
            const matchingPreset = this.notePresets.find(p => p.value === rawValue);
            if (matchingPreset) {
              this.selectedPreset = rawValue;
              this.inputControl.reset();
            } else {
              this.selectedPreset = 'Egyéni';
              this.inputControl.setValue(rawValue);
            }
          } else {
            this.inputControl.setValue(rawValue);
          }
        } else {
          this.inputControl.setValue(newVal);
        }
      } else {
        // Üres érték esetén töröljük a formot (pl. new note day megnyitásakor)
        this.selectedNoteColor = 'orange'; // Alapértelmezett szín visszaállítása
        this.selectedPreset = null;
        this.inputControl.reset();
        this.inputControl.markAsUntouched();
        this.inputControl.markAsPristine();
      }
    }
  }

  // Dynamic validator based on input type
  dynamicValidator(control: any) {
    if (!control.value) return null;
    
    if (this.inputType === 'phone') {
      return this.phoneValidator(control);
    } else if (this.inputType === 'email') {
      return this.emailValidator(control);
    } else if (this.inputType === 'official') {
      return this.officialValidator(control);
    } else if (this.inputType === 'notes') {
      return this.notesValidator(control);
    }
    
    return null;
  }

  phoneValidator(control: any) {
    return phoneFormValidator(control);
  }

  emailValidator(control: any) {
    return emailFormValidator(control);
  }

  officialValidator(control: any) {
    if (!control.value) return null;
    
    const value = control.value.trim();
    
    // Basic validation for official name - must not be empty and should have reasonable length
    if (value.length < 2) {
      return { invalid: true };
    }
    
    if (value.length > 100) {
      return { invalid: true };
    }
    
    return null;
  }

  notesValidator(control: any) {
    if (!control.value) return null;
    
    const value = control.value.trim();
    
    // Jegyzet validáció - 1-6 karakter között
    if (value.length < 1 || value.length > 6) {
      return { invalid: true };
    }
    
    return null;
  }

  onInput() {
    if (this.inputType === 'phone') {
      this.onPhoneInput();
    } else {
      if (this.inputType === 'email') {
        // Validáció újrafuttatása beillesztés/módosítás után
        this.inputControl.updateValueAndValidity();
        this.inputControl.markAsTouched();
      }
      // Csak nem-telefon típusnál emittáljuk közvetlenül
      this.inputValueChange.emit(this.inputControl.value || '');
    }
  }

  /** Beillesztés (paste) esemény kezelése - validáció kényszerítése */
  onPaste() {
    setTimeout(() => {
      if (this.inputType === 'email') {
        this.inputControl.updateValueAndValidity();
        this.inputControl.markAsTouched();
        this.inputControl.markAsDirty();
      }
    }, 0);
  }

  onPhoneInput() {
    const input = this.inputField?.nativeElement;
    const prevValue = this.inputControl.value || '';
    const prevCursorPos = input?.selectionStart ?? prevValue.length;
    
    const cleaned = cleanPhoneValue(prevValue);
    const formatted = formatPhoneNumber(cleaned);
    const newCursorPos = calculateCursorPosition(formatted, prevValue, prevCursorPos);

    // FormControl és DOM frissítés
    this.inputControl.setValue(formatted, { emitEvent: false });
    if (input) {
      input.value = formatted;
      input.setSelectionRange(newCursorPos, newCursorPos);
    }
    
    // Formázott érték emittálása a parent felé
    this.inputValueChange.emit(formatted);
  }

  saveNewPhone() {
    if (this.inputControl.valid && this.inputControl.value?.trim()) {
      this.phoneAdded.emit(this.inputControl.value.trim());
      this.resetForm();
      this.closeMenuIfAvailable();
    }
  }

  // New universal save method
  saveValue() {
    // Preset módban: preset kiválasztva (nem Egyéni) → direkt mentés input validáció nélkül
    if (this.notePresets !== null && this.selectedPreset !== null && this.selectedPreset !== 'Egyéni') {
      const emitValue = this.selectedNoteColor === 'red'
        ? 'red:' + this.selectedPreset
        : this.selectedPreset;
      this.valueAdded.emit(emitValue);
      if (this.showClearPlanCheckbox && this.clearPlanChecked) {
        this.clearPlanRequested.emit();
      }
      this.resetForm();
      setTimeout(() => {
        this.closeMenuIfAvailable();
      }, 10);
      return;
    }

    if (this.inputControl.valid && this.inputControl.value?.trim()) {
      const value = this.inputControl.value.trim();
      
      // Emit specific events (and generic valueAdded for all types)
      if (this.inputType === 'phone') {
        this.phoneAdded.emit(value);
      } else if (this.inputType === 'email') {
        this.emailAdded.emit(value);
      } else if (this.inputType === 'official') {
        this.officialAdded.emit(value);
      }
      // Notes típusnál szín prefixet adunk hozzá ha piros
      const emitValue = (this.inputType === 'notes' && this.selectedNoteColor === 'red')
        ? 'red:' + value
        : value;
      this.valueAdded.emit(emitValue);
      if (this.showClearPlanCheckbox && this.clearPlanChecked) {
        this.clearPlanRequested.emit();
      }
      
      this.resetForm();
      // Add a small delay to ensure events are processed before closing menu
      setTimeout(() => {
        this.closeMenuIfAvailable();
      }, 10);
    }
  }

  cancelAddPhone() {
    this.resetForm();
    this.cancelled.emit();
    this.closeMenuIfAvailable();
  }

  // New universal cancel method
  cancelInput() {
    this.resetForm();
    this.cancelled.emit();
    this.closeMenuIfAvailable();
  }

  // Delete handler method
  onDeleteClicked() {
    this.deleteClicked.emit();
    this.closeMenuIfAvailable();
  }

  private closeMenuIfAvailable() {
    if (this.menuTrigger) {
      // Use the provided menu trigger - this is the safest method
      this.menuTrigger.closeMenu();
    } else {
      // CRITICAL: NO FALLBACK that clicks backdrops!
      // This prevents accidental dialog closure
      console.warn('SmallInputMenuComponent: menuTrigger not provided - menu cannot be closed safely. This may cause the menu to remain open.');
      
      // Safe alternative: try to programmatically trigger the menu close without clicking anything
      setTimeout(() => {
        // Find our specific menu by class and dispatch Escape key only to that element
        const specificMenu = document.querySelector('.mat-mdc-menu-panel.add-official-menu');
        if (specificMenu) {
          const escEvent = new KeyboardEvent('keydown', {
            key: 'Escape',
            code: 'Escape',
            keyCode: 27,
            which: 27,
            bubbles: true,
            cancelable: true
          });
          specificMenu.dispatchEvent(escEvent);
        }
      }, 10);
    }
  }

  private resetForm() {
    this.inputControl.reset();
    this.selectedNoteColor = 'orange'; // Szín visszaállítása alapértelmezettbe
    this.selectedPreset = null; // Preset visszaállítása
    this.clearPlanChecked = false; // Tervszámok törlése checkbox visszaállítása
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.saveValue();
    } else if (event.key === 'Escape') {
      this.cancelInput();
    } else if (this.inputType === 'phone') {
      // Telefonszámnál blokkolja a további számjegy bevitelt ha már elértük a 9 számjegyet
      this.blockExcessPhoneDigits(event);
    } else if (this.inputType === 'email') {
      // Email címnél ékezetes karakterek tiltása
      this.blockAccentedCharsInEmail(event);
    }
  }

  /** Megakadályozza nem szám karakterek és 9-nél több számjegy bevitelét */
  private blockExcessPhoneDigits(event: KeyboardEvent) {
    blockExcessPhoneDigitsOnKeyDown(event, this.inputControl.value || '');
  }

  /** Nem-ASCII karakterek bevitelének megakadályozása email mezőben */
  private blockAccentedCharsInEmail(event: KeyboardEvent) {
    blockAccentedCharsOnKeyDown(event);
  }

  /** Nem-ASCII karakterek eltávolítása email értékből (pl. beillesztés után) */
  private stripAccentedCharsFromEmail() {
    const value = this.inputControl.value || '';
    const cleaned = value.split('').filter((ch: string) => ch.charCodeAt(0) <= 127).join('');
    if (cleaned !== value) {
      this.inputControl.setValue(cleaned, { emitEvent: false });
    }
  }

  // Getters for template convenience
  get currentLabel(): string {
    if (this.label) return this.label;
    if (this.inputType === 'phone') return 'Új telefonszám';
    if (this.inputType === 'email') return 'Új email cím';
    if (this.inputType === 'official') return 'Új tisztségviselő';
    if (this.inputType === 'notes') return 'Jegyzet';
    return 'Új érték';
  }

  get currentPlaceholder(): string {
    if (this.placeholder) return this.placeholder;
    if (this.inputType === 'phone') return '+36 XX XXX XXXX';
    if (this.inputType === 'email') return 'pelda@email.com';
    if (this.inputType === 'official') return 'Név';
    if (this.inputType === 'notes') return 'Max 6 karakter';
    return '';
  }

  get currentInputType(): string {
    if (this.inputType === 'phone') return 'tel';
    return 'text'; // email és egyéb típusoknál text, saját validálással
  }

  get errorMessage(): string {
    if (this.inputType === 'phone') {
      return 'Érvényes magyar mobil szám formátum: +36 XX XXX XXXX';
    } else if (this.inputType === 'email') {
      if (this.inputControl.hasError('accented')) {
        return 'Ékezetes karakterek nem megengedettek az email címben';
      }
      return 'Érvényes email cím formátum szükséges';
    } else if (this.inputType === 'official') {
      return 'Kérem adjon meg egy 2-100 karakter hosszú nevet és beosztást';
    } else if (this.inputType === 'notes') {
      return '1-6 karakter hosszú jegyzet szükséges';
    }
    return 'Érvényes érték szükséges';
  }

  /** Mentés gomb engedélyezett-e */
  get isSaveEnabled(): boolean {
    if (this.notePresets !== null) {
      if (this.selectedPreset === null) return false;
      if (this.selectedPreset !== 'Egyéni') return true;
    }
    return !!(this.inputControl.value && this.inputControl.value.trim() &&
              !this.inputControl.hasError('invalid') && !this.inputControl.hasError('accented'));
  }
}
